import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";

/**
 * Small button that hits an /api endpoint returning a binary xlsx blob and
 * triggers a browser download. Uses the axios `api` client so auth headers +
 * base URL are consistent with the rest of the app.
 */
export default function ExportXlsxButton({
  url,                    // e.g. "/staff/exports/purchase-orders.xlsx"
  params,                 // {outlet, status, from, to}
  label = "Export .xlsx",
  filename = "export.xlsx",
  testId = "export-xlsx",
  className = "",
}) {
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get(url, { params, responseType: "blob" });
      // Prefer server-supplied filename from Content-Disposition, fall back to caller value.
      let name = filename;
      const cd = res.headers?.["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      if (match) name = match[1];
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(objectUrl);
      toast.success(`${name} downloaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    } finally { setBusy(false); }
  };

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={download}
      disabled={busy}
      className={`inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded border border-[hsl(var(--rule))] hover:border-[hsl(var(--forest))] hover:text-[hsl(var(--forest))] transition-colors disabled:opacity-60 ${className}`}
    >
      {busy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
      <span>{busy ? "Preparing…" : label}</span>
    </button>
  );
}
